import React, { useState } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  Collapse,
  List,
} from '@mui/material';
import {
  AccountCircle,
  Logout,
  Person,
  Login,
  Check,
  AutoAwesome,
  ChevronRight,
  ExpandMore,
  DensitySmall,
  DensityMedium,
  DensityLarge,
  Laptop,
  Computer,
  TabletMac,
  Tv,
} from '@mui/icons-material';
import { useAuth } from '../shared/hooks/useAuth';
import { useThemeSettings } from '../styles/contexts/ThemeContext';
import { useMenuStyles } from '../styles/utilities/hooks';
import ProfileEditDialog from '../features/users/components/ProfileEditDialog';

const HeaderMenu = ({ onLoginClick }) => {
  const theme = useTheme();
  const menuStyles = useMenuStyles();
  const { user, logout, updateProfile } = useAuth();
  const { density, setDensity, resetDensity, densityOptions } = useThemeSettings();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [densitySubmenuOpen, setDensitySubmenuOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  
  const open = Boolean(anchorEl);
  const isAuthenticated = !!user;
  
  // Helper function for short density descriptions
  const getShortDescription = (value) => {
    const shortDescriptions = {
      'compact': 'Small',
      'comfortable': 'Default',
      'standard': 'Medium',
      'spacious': 'Large',
      'large': 'Extra Large'
    };
    return shortDescriptions[value] || '';
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setDensitySubmenuOpen(false);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setDensitySubmenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    handleClose();
  };

  const handleEditProfile = () => {
    setProfileDialogOpen(true);
    handleClose();
  };

  const handleProfileUpdate = async (updatedUser) => {
    // Refresh user data in auth context after successful update
    if (updatedUser) {
      await updateProfile();
    }
  };

  const handleDensitySelect = (value) => {
    setDensity(value);
    handleClose();
  };

  const handleAutoDetect = () => {
    resetDensity();
    handleClose();
  };

  const toggleDensitySubmenu = (e) => {
    e.stopPropagation();
    setDensitySubmenuOpen(!densitySubmenuOpen);
  };

  const getDensityIcon = (value, selected = false) => {
    const iconProps = {
      fontSize: 'small',
      color: selected ? 'primary' : 'inherit'
    };
    
    switch (value) {
      case 'compact':
        return <Laptop {...iconProps} />;
      case 'comfortable':
        return <Computer {...iconProps} />;
      case 'standard':
        return <Computer {...iconProps} />;
      case 'spacious':
        return <TabletMac {...iconProps} />;
      case 'large':
        return <Tv {...iconProps} />;
      default:
        return <Computer {...iconProps} />;
    }
  };

  const getMainDensityIcon = () => {
    switch (density) {
      case 'compact':
      case 'comfortable':
        return <DensitySmall fontSize="small" />;
      case 'standard':
        return <DensityMedium fontSize="small" />;
      case 'spacious':
      case 'large':
        return <DensityLarge fontSize="small" />;
      default:
        return <DensityMedium fontSize="small" />;
    }
  };

  const displayName = isAuthenticated 
    ? (user?.fullname || user?.username || 'User')
    : 'Guest';

  return (
    <>
      <Button
        onClick={handleClick}
        sx={menuStyles.trigger}
        endIcon={<ExpandMore />}
      >
        <AccountCircle />
        {displayName}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: menuStyles.paper
        }}
      >
        {/* User Section */}
        {isAuthenticated ? [
          <MenuItem 
            key="user-name" 
            dense 
            sx={{ 
              cursor: 'default',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            <Typography variant="body2" sx={{ 
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.palette.text.primary
            }}>
              {user?.fullname || user?.username || 'User'}
            </Typography>
          </MenuItem>,
          <MenuItem 
            key="user-role" 
            dense 
            sx={{ 
              cursor: 'default',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {user?.role || (user?.is_admin ? 'Admin' : 'User')}
            </Typography>
          </MenuItem>
        ] : (
          <MenuItem 
            dense 
            sx={{ 
              cursor: 'default',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Not logged in
            </Typography>
          </MenuItem>
        )}
        
        <Divider sx={menuStyles.divider} />
        
        {/* Display Settings Section */}
        <MenuItem onClick={handleAutoDetect}>
          <ListItemIcon>
            <AutoAwesome fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Auto-detect density" />
        </MenuItem>
        
        <MenuItem onClick={toggleDensitySubmenu}>
          <ListItemIcon>
            {getMainDensityIcon()}
          </ListItemIcon>
          <ListItemText primary="Display Density" />
          {densitySubmenuOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
        </MenuItem>
        
        <Collapse in={densitySubmenuOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding sx={menuStyles.nestedList}>
            {densityOptions.map((option) => (
              <MenuItem
                dense
                key={option.value}
                onClick={() => handleDensitySelect(option.value)}
                selected={option.value === density}
                sx={{ pl: theme.spacing(4) }}
              >
                <ListItemIcon>
                  {getDensityIcon(option.value, option.value === density)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2">
                        {option.name}
                      </Typography>
                      {option.value === density && (
                        <Check sx={{ fontSize: 14, color: 'primary.main' }} />
                      )}
                    </Box>
                  }
                  secondary={getShortDescription(option.value)}
                />
              </MenuItem>
            ))}
          </List>
        </Collapse>
        
        <Divider sx={menuStyles.divider} />
        
        {/* Actions Section */}
        {isAuthenticated ? [
          <MenuItem key="profile" onClick={handleEditProfile}>
            <ListItemIcon>
              <Person />
            </ListItemIcon>
            <ListItemText primary="Edit Profile" />
          </MenuItem>,
          <MenuItem key="logout" onClick={handleLogout}>
            <ListItemIcon>
              <Logout />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        ] : (
          <MenuItem onClick={() => {
            handleClose();
            onLoginClick && onLoginClick();
          }}>
            <ListItemIcon>
              <Login />
            </ListItemIcon>
            <ListItemText primary="Login" />
          </MenuItem>
        )}
      </Menu>

      {/* Profile Edit Dialog */}
      {isAuthenticated && (
        <ProfileEditDialog
          open={profileDialogOpen}
          onClose={() => setProfileDialogOpen(false)}
          user={user}
          isAdminMode={false}
          onSave={handleProfileUpdate}
        />
      )}
    </>
  );
};

export default HeaderMenu;